/*----------------------------------
- SECTION: GATED APPRAISE COMPOSER
----------------------------------*/

/**
 * @docs docs/features/website-product-feature-pages
 * @adr  ADR-0050
 * @rule The gate declares no persona. The visitor never answered the question here, so the
 *       wall skips it and Product onboarding asks it after signup.
 */

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import React from 'react';

import AppraiseComposer from '@uniquedomains/components/domains/appraisal/AppraiseComposer';
import {
    APPRAISAL_PROVIDERS,
    getDefaultAppraisalProviderIds,
    parseAppraisalTokens,
    type AppraisalProviderId,
} from '@uniquedomains/core/domains/appraisal';
import {
    APPRAISAL_RUN_ACTION,
    APPRAISAL_RUN_SIZE_FEATURE,
} from '@uniquedomains/components/contracts/upgrade/catalogs/featureActions';

import { buildWebsiteAuthHref, openWebsiteAuthHref } from '@/client/utils/conversion/authHref';
import { trackConversionTrigger } from '@/client/pages/landing/lib/trackConversionTrigger';
import { trackLandingCta } from '@/client/pages/landing/lib/trackLandingCta';

/*----------------------------------
- TYPES
----------------------------------*/

type AppraiseComposerSectionProps = {
    page: string;
    /** Placement in the hero grid. The card itself is the shared product component. */
    class?: string;
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

const SIGNUP_ATTRIBUTION = 'website_product_appraise_composer';

/*----------------------------------
- HELPERS
----------------------------------*/

/**
 * Where every gated action on this page lands: the "Are you human?" wall for an appraisal run.
 *
 * It used to be the investor persona signup link, which declared an investor persona with a
 * `chosen` source on the visitor's behalf, though nobody on this page picks one.
 * The run gate is named instead, and Product onboarding asks the persona (ADR-0050).
 */
export const buildAppraiseGateHref = () => buildWebsiteAuthHref({
    flow: 'cold',
    mode: 'signup',
    from: SIGNUP_ATTRIBUTION,
    feature: APPRAISAL_RUN_SIZE_FEATURE,
    action: APPRAISAL_RUN_ACTION,
});

/**
 * Tracking for one gated action, exported so the phone action bar's primary button
 * is the composer's run button rather than a second, half-measured path to the same
 * signup: same href, same landing CTA row, same conversion trigger.
 */
export const trackAppraiseGate = (
    { page, action, href, names = 0 }: { page: string; action: string; href: string; names?: number },
) => {
    trackLandingCta({
        page,
        section: 'composer',
        label: action,
        kind: 'composer',
        href,
        surface: 'product_appraise_page',
    });
    trackConversionTrigger('website_product_appraise_composer_gate', {
        href,
        page,
        section: 'composer',
        context: { action, names },
    });
};

/*----------------------------------
- COMPONENT
----------------------------------*/

/**
 * The composer is fully interactive for anonymous visitors: names can be typed,
 * pasted and removed, and sources toggled. Only the actions that would run an
 * appraisal or read private data are gated, so the visitor understands the tool
 * before being asked for an account.
 *
 * The run card counts names, never credits (ADR-0083). No estimate is ever
 * rendered on this page.
 *
 * It sits in the hero's second column, so the card is placed by the hero grid and
 * carries no section spacing of its own.
 */
const AppraiseComposerSection = ({ page, class: className }: AppraiseComposerSectionProps) => {
    const [names, setNames] = React.useState<string[]>([]);
    const [draft, setDraft] = React.useState('');
    const [selected, setSelected] = React.useState<AppraisalProviderId[]>(() => getDefaultAppraisalProviderIds());

    const providers = APPRAISAL_PROVIDERS.map((provider) => ({
        id: provider.id,
        label: provider.label,
        initial: provider.initial,
        status: provider.status,
        tooltip: provider.tooltip,
        selected: selected.includes(provider.id),
    }));

    const gate = (action: string) => {
        const href = buildAppraiseGateHref();
        trackAppraiseGate({ page, action, href, names: names.length });
        /* In place, like every other wall: the typed names stay on screen behind the modal. The
           assign is the fallback for a browser without CustomEvent. */
        if (typeof window !== 'undefined' && !openWebsiteAuthHref(href)) window.location.assign(href);
    };

    const toggleProvider = (id: AppraisalProviderId) => {
        setSelected((current) => {
            if (!current.includes(id)) return [...current, id];
            // At least one source must stay on, otherwise a run means nothing.
            if (current.length <= 1) return current;
            return current.filter((entry) => entry !== id);
        });
    };

    return (
        <div data-reveal class={className}>
            <AppraiseComposer
                testIdPrefix="website-appraise"
                names={names}
                draft={draft}
                providers={providers}
                submitState="gated"
                onDraftChange={setDraft}
                onAddNames={(raw) => setNames((current) => [...current, ...parseAppraisalTokens(raw, current).names])}
                onRemoveName={(name) => setNames((current) => current.filter((entry) => entry !== name))}
                onToggleProvider={toggleProvider}
                onToggleAllProviders={() => setSelected((current) => {
                    // Same rule as the product: clearing means one source, not none.
                    const live = getDefaultAppraisalProviderIds();
                    return live.every((id) => current.includes(id)) ? live.slice(0, 1) : live;
                })}
                onOpenImport={(kind) => gate(`import_${kind}`)}
                onSubmit={() => gate('run')}
                onSubmitSingle={() => gate('run')}
            />
        </div>
    );
};

export default AppraiseComposerSection;
